import { z } from "zod";
import { get, typedEntries } from "~/lib/types/utils";
import { testFieldAgainstAllDependencies, testFieldAgainstDependencyOperator } from "./form-config-types";
import type {
  FormConfig,
  FormConfigInPages,
  ObjectConfig,
  ObjectConfigValues,
  I18NFunction,
} from "./form-config-types";
import { comprehensiveErrorMap } from "./data/form-data-utils";

export function formConfigInPagesToZodSchemas(config: FormConfigInPages, l: I18NFunction) {
  // return formConfigToZodSchema(Object.assign({}, ...config.flatMap((page) => page.formFields as any)));
  // loop over the config and return a zod schema for each page
  const zodSchemas = config.map((page) => {
    return formConfigToZodSchemas(page.formFields as any, l);
  });

  return zodSchemas;
}

export function getFormConfigDefaultValues(config: FormConfig) {
  function getDefaultValueForObject(config: ObjectConfig) {
    const keyValues = Object.entries(config.properties).map(([key, value]) => {
      return {
        [key]: "defaultValue" in value ? value.defaultValue : undefined,
      };
    });

    return Object.assign({}, ...keyValues);
  }

  //
  // loop over the config
  //
  const asdf = Object.entries(config).map(([key, value]) => {
    if (value.type === "object") {
      return {
        [key]: getDefaultValueForObject(value),
      };
    }

    return {
      [key]: "defaultValue" in value ? value.defaultValue : undefined,
    };
  });

  const obj = Object.assign({}, ...asdf);

  return obj;
}

/**
 *
 *
 *
 * generate the schema by looping over the config. When a field has a type of "object", we need to recursively generate the schema for the nested fields.
 *
 *
 */
export function formConfigToZodSchemas(
  config: FormConfig,
  l: I18NFunction,
): {
  default: z.ZodEffects<z.ZodObject<any>>;
  partial: z.ZodEffects<z.ZodObject<any>>;
} {
  // let allOptional = true;
  //
  // get the schema for the object
  //
  function getKeySchemaForObject(config: ObjectConfig) {
    const keyValues = Object.entries(config.properties).map(([key, value]) => {
      // if (!value.schema.isOptional()) {
      //   allOptional = false;
      // }

      return {
        [key]: value.schema,
      };
    });

    return Object.assign({}, ...keyValues);
  }

  //
  // loop over the config
  //
  const asdf = Object.entries(config).map(([key, value]) => {
    if (value.type === "object") {
      return {
        [key]: getKeySchemaForObject(value),
      };
    }

    // if (!value.schema.isOptional()) {
    //   allOptional = false;
    // }

    return {
      [key]: value.schema,
    };
  });

  const regularInnerObjectSchema = Object.assign({}, ...asdf);
  const partialInnerObjectSchema = Object.assign({}, ...asdf);

  //
  // for each object, we need to wrap in a z.object
  //
  Object.entries(regularInnerObjectSchema).forEach(([key, value]) => {
    // if this is already a Zod type, skip
    // @ts-expect-error value is of type unknown
    if ("parse" in value) {
      return;
    }

    // @ts-expect-error value is of type unknown
    // also make the schema partial (for auto-save)
    regularInnerObjectSchema[key] = z.object(value);
    // @ts-expect-error value is of type unknown
    // also make the schema partial (for auto-save)
    partialInnerObjectSchema[key] = z.object(value).partial();

    // // make optional, if all the fields inside the object are optional
    // if (allOptional) {
    //   // @ts-expect-error value is of type unknown
    //   obj[key] = z.object(value).optional();
    // } else {
    //   // @ts-expect-error value is of type unknown
    //   obj[key] = z.object(value, { required_error: "This field is require!!!d" });
    // }
  });

  //
  //
  // add the requiredIf to the schema
  //
  function superRefine(data: Record<string, any>, ctx: z.RefinementCtx) {
    function addSuperRefine(config: ObjectConfig["properties"]) {
      Object.entries(config).forEach(([key, fieldConfig]) => {
        //
        // requireIf
        //
        // if (fieldConfig.requireIf) {
        //   const [pathToDependency, operator, valueToCheckAgainst] = fieldConfig.requireIf;
        //   const doesFieldPassDependencyCheck = testFieldAgainstDependencyOperator({
        //     operator,
        //     valueToCheckAgainst,
        //     dependencyValue: get(data, pathToDependency),
        //   });

        //   const thisFieldValue = get(data, key);
        //   const isThisFieldEmpty = !thisFieldValue || thisFieldValue === "";

        //   // if the dependency check fails, skip, as we don't need to check if the field is required
        //   if (!doesFieldPassDependencyCheck) {
        //     return;
        //   }

        //   if (isThisFieldEmpty) {
        //     return ctx.addIssue({
        //       code: z.ZodIssueCode.custom,
        //       path: [key],
        //       // message: l("This field is required", "Ce champ est requis"),
        //       message: "This field is required",
        //     });
        //   }
        // }

        if (fieldConfig.dependencies) {
          const doesFieldPassAllDependencies = testFieldAgainstAllDependencies({
            testFor: "require",
            dependencies: fieldConfig.dependencies,
            getDependencyValue: (pathToDependency: string) => {
              return get(data, pathToDependency);
            },
          });

          const thisFieldValue = get(data, key);
          const isThisFieldEmpty =
            !thisFieldValue ||
            thisFieldValue === "" ||
            (Array.isArray(thisFieldValue) && thisFieldValue.length === 0);

          // if the dependency check fails, skip, as we don't need to check if the field is required
          if (!doesFieldPassAllDependencies) {
            return;
          }

          if (isThisFieldEmpty) {
            const errorPath = key.split(".");
            return ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: errorPath,
              message: l("This field is required", "Ce champ est requis"),
            });
          }
        }
      });
    }

    Object.entries(config).forEach(([key, value]) => {
      if (value.type === "object") {
        // addSuperRefine(value.properties);
        Object.entries(value.properties).forEach(([nestedKey, fieldConfig]) => {
          addSuperRefine({ [`${key}.${nestedKey}`]: fieldConfig });

          // Special validation for ranking fields inside objects
          if (fieldConfig.type === "ranking") {
            const requiredItems = fieldConfig.items.filter((item) => !item.optional).map((item) => item.key);
            const rankedItems = get(data, `${key}.${nestedKey}`) as string[] | undefined;

            if (requiredItems.length > 0) {
              const missingRequired = requiredItems.filter((itemKey) => !rankedItems?.includes(itemKey));

              if (missingRequired.length > 0) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [key, nestedKey],
                  message: l("All required items must be ranked", "Tous les éléments requis doivent être classés"),
                });
              }
            }
          }
        });
      } else {
        addSuperRefine({ [key]: value });

        // Special validation for ranking fields at root level
        if (value.type === "ranking") {
          const requiredItems = value.items.filter((item) => !item.optional).map((item) => item.key);
          const rankedItems = get(data, key) as string[] | undefined;

          if (requiredItems.length > 0) {
            const missingRequired = requiredItems.filter((itemKey) => !rankedItems?.includes(itemKey));

            if (missingRequired.length > 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [key],
                message: l("All required items must be ranked", "Tous les éléments requis doivent être classés"),
              });
            }
          }
        }
      }
    });
  }

  // Set comprehensive error map for user-friendly error messages
  // This provides fallback error messages for schemas that don't have their own errorMap
  z.setErrorMap(comprehensiveErrorMap(l));

  const defaultZodSchema = z.object(regularInnerObjectSchema).superRefine(superRefine);
  const partialZodSchema = z.object(partialInnerObjectSchema).partial().superRefine(superRefine);

  return {
    default: defaultZodSchema,
    partial: partialZodSchema,
  };
}
